import React, { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Button } from '@mui/material';
import { navigateToSection } from 'app/services/loanNavigation/loanNavigation';
import PropTypes from 'prop-types';

import { FeatureFlag } from 'constants/featureFlags';

import { selectIncompleteLinkedSections } from 'store/loanApplications/selectors';

import FieldCompletenessIndicator from '../FieldCompletenessIndicator';

import { getSectionWrapperStyles, goToSectionButtonStyles } from './SectionPresenceWrapper.styles';
import { SectionPresenceWrapperTestIds } from './SectionPresenceWrapper.testIds';

/**
 * Section-level counterpart to FieldPresenceWrapper (DEV-4467). Looks up a
 * checklist item linked to this section/area via its full area id and, when the
 * item is failing, renders the shared "Needed" indicator plus a "Go to section"
 * shortcut. Renders nothing when the section has no failing link.
 */
const SectionPresenceWrapper = ({ areaId = '' }) => {
  const history = useHistory();
  const { applicationId } = useParams();
  // "Go to field" resolves the Underwriting tab route against this toggle (LEN2-499).
  const isRestyledUnderwritingTabEnabled = useFeatureToggle(
    FeatureFlag.EnableRestyledUnderwritingTab,
  );

  const incompleteLinkedSections = useSelector(selectIncompleteLinkedSections());

  const onGoToSection = useCallback(() => {
    navigateToSection({
      parameters: { areaId },
      context: { history, applicationId, isRestyledUnderwritingTabEnabled },
    });
  }, [areaId, history, applicationId, isRestyledUnderwritingTabEnabled]);

  const completenessEntry = areaId ? incompleteLinkedSections[areaId] : null;

  if (!completenessEntry) {
    return null;
  }

  const isSectionNeeded = !completenessEntry.isOverridden;

  return (
    <Box sx={getSectionWrapperStyles({ isSectionNeeded })}>
      <FieldCompletenessIndicator
        checklistId={completenessEntry.checklistId}
        isOverridden={completenessEntry.isOverridden}
        itemId={completenessEntry.itemId}
      />
      {isSectionNeeded && (
        <Button
          disableRipple
          data-testid={SectionPresenceWrapperTestIds.GO_TO_SECTION_BUTTON}
          size="small"
          sx={goToSectionButtonStyles}
          type="button"
          variant="text"
          onClick={onGoToSection}
        >
          Go to section
        </Button>
      )}
    </Box>
  );
};

SectionPresenceWrapper.propTypes = {
  areaId: PropTypes.string,
};

export default memo(SectionPresenceWrapper);
